import { useCallback, useEffect, useState } from "react";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { n0, n2 } from "../../lib/format";
import { Notice, Stat } from "../../lib/ui";
import "./finance.css";

/*
 * Trial Balance – port of trial_balance.php.  GET /api/reports/trial-balance (backend/reports.py)
 * Same filters (search, account type, from/to date), sortable columns, totals, balanced check
 * (difference < 0.01), summary by account type, both charts, print and CSV export.
 */

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16"];
const today = () => new Date().toISOString().slice(0, 10);
const longDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "2-digit", year: "numeric" }) : "");
const shortDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }) : "");

function Donut({ data }) {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  let angle = -Math.PI / 2;
  const arcs = data.map((d, i) => {
    const sweep = (d.value / total) * Math.PI * 2;
    const a0 = angle, a1 = angle + sweep;
    angle = a1;
    const large = sweep > Math.PI ? 1 : 0;
    const p = (a, r) => [80 + r * Math.cos(a), 80 + r * Math.sin(a)];
    const [x0, y0] = p(a0, 70), [x1, y1] = p(a1, 70), [x2, y2] = p(a1, 42), [x3, y3] = p(a0, 42);
    const path = sweep >= Math.PI * 2 - 1e-6
      ? "M80 10 A70 70 0 1 1 79.99 10 L79.99 38 A42 42 0 1 0 80 38 Z"
      : `M${x0} ${y0} A70 70 0 ${large} 1 ${x1} ${y1} L${x2} ${y2} A42 42 0 ${large} 0 ${x3} ${y3} Z`;
    return <path key={d.label} d={path} fill={COLORS[i % COLORS.length]}><title>{`${d.label}: TZS ${n0(d.value)}`}</title></path>;
  });
  return (
    <div className="tb-donut">
      <svg viewBox="0 0 160 160" role="img" aria-label="Balance by account type">{arcs}</svg>
      <ul>
        {data.map((d, i) => (
          <li key={d.label}><span style={{ background: COLORS[i % COLORS.length] }} />{d.label}<b className="mono">{n0(d.value)}</b></li>
        ))}
      </ul>
    </div>
  );
}

function Bars({ data }) {
  const max = Math.max(1, ...data.flatMap((d) => [d.debit, d.credit]));
  return (
    <div className="tb-bars" role="img" aria-label="Debit and credit by account type">
      <div className="tb-legend"><span className="d" />Debit <span className="c" />Credit</div>
      {data.map((d) => (
        <div key={d.type} className="tb-bar-row">
          <span className="tb-bar-label">{d.type}</span>
          <div className="tb-bar-pair">
            <div className="tb-bar d" style={{ width: `${(d.debit / max) * 100}%` }} title={`Debit: TZS ${n0(d.debit)}`} />
            <div className="tb-bar c" style={{ width: `${(d.credit / max) * 100}%` }} title={`Credit: TZS ${n0(d.credit)}`} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function TrialBalance() {
  const api = useApi();
  const [filters, setFilters] = useState({ search: "", account_type: "", date_from: "", date_to: today(), sort: "account_number", order: "ASC" });
  const [form, setForm] = useState(filters);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async (f) => {
    setError(null);
    try { setData(await api.get("/api/reports/trial-balance", f)); }
    catch (e) { setError(e.message); }
  }, [api]);
  useEffect(() => { load(filters); }, [load, filters]);

  const sortBy = (col) => setFilters((f) => ({ ...f, sort: col, order: f.sort === col && f.order === "ASC" ? "DESC" : "ASC" }));
  const sortIcon = (col) => (filters.sort !== col ? "fa-sort" : filters.order === "ASC" ? "fa-sort-up" : "fa-sort-down");
  const exportUrl = () => {
    const u = new URL(api.base + "/api/reports/trial-balance");
    Object.entries({ ...filters, export: "csv" }).forEach(([k, v]) => u.searchParams.set(k, v));
    return u.toString();
  };

  const Th = ({ col, children, num }) => (
    <th className={`sortable ${num ? "num" : ""}`} onClick={() => sortBy(col)} aria-sort={filters.sort === col ? (filters.order === "ASC" ? "ascending" : "descending") : "none"}>
      {children} <i className={`fas ${sortIcon(col)}`} style={{ opacity: filters.sort === col ? 1 : 0.35 }} />
    </th>
  );

  const actions = (
    <>
      <button type="button" className="btn" onClick={() => window.print()}><i className="fas fa-print" /> Print</button>
      <a className="btn" href={exportUrl()}><i className="fas fa-file-csv" /> Export CSV</a>
    </>
  );

  return (
    <main className="pg tb">
      <PageHeader slug="trial_balance" actions={actions} />
      <Notice notice={error && { tone: "err", text: `Couldn't load the trial balance: ${error}` }} />

      {data && (
        <>
          <div className={`tb-status ${data.balanced ? "ok" : "bad"}`}>
            <i className={`fas ${data.balanced ? "fa-circle-check" : "fa-triangle-exclamation"}`} />
            <div>
              <h2>{data.balanced ? "Books are balanced" : "Balance discrepancy detected"}</h2>
              <p>{data.balanced ? "Debit and credit totals match." : `Difference: TZS ${n2(data.difference)}`}</p>
            </div>
            {data.filters.date_to && <div className="tb-asof"><span>As of</span><strong>{longDate(data.filters.date_to)}</strong></div>}
          </div>

          <div className="ui-stats">
            <Stat icon="fa-coins" tone="brand" label="Total debit" value={n0(data.total_debit)} unit="TZS" />
            <Stat icon="fa-hand-holding-dollar" tone="danger" label="Total credit" value={n0(data.total_credit)} unit="TZS" />
            <Stat icon="fa-list" tone="muted" label="Active accounts" value={data.accounts.length} />
            <Stat icon={data.balanced ? "fa-scale-balanced" : "fa-circle-exclamation"} tone={data.balanced ? "up" : "warn"} label="Difference" value={n2(data.difference)} unit="TZS" />
          </div>

          {data.type_stats.length > 0 && (
            <div className="tb-charts">
              <section className="panel tb-chart"><h3>Balance by account type</h3>
                <Donut data={data.type_stats.map((s) => ({ label: s.type, value: s.debit + s.credit }))} />
              </section>
              <section className="panel tb-chart"><h3>Debit vs credit by type</h3><Bars data={data.type_stats} /></section>
            </div>
          )}
        </>
      )}

      <div className="panel" style={{ marginBottom: "1.25rem" }}>
        <form className="toolbar" onSubmit={(e) => { e.preventDefault(); setFilters((f) => ({ ...f, ...form })); }}>
          <div className="field grow"><label htmlFor="tb-q">Search</label>
            <input id="tb-q" value={form.search} onChange={(e) => setForm({ ...form, search: e.target.value })} placeholder="Account name or number" /></div>
          <div className="field"><label htmlFor="tb-type">Account type</label>
            <select id="tb-type" value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })}>
              <option value="">All account types</option>
              {(data?.account_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
            </select></div>
          <div className="field"><label htmlFor="tb-from">From</label>
            <input id="tb-from" type="date" value={form.date_from} onChange={(e) => setForm({ ...form, date_from: e.target.value })} /></div>
          <div className="field"><label htmlFor="tb-to">To</label>
            <input id="tb-to" type="date" value={form.date_to} onChange={(e) => setForm({ ...form, date_to: e.target.value })} /></div>
          <button type="submit" className="btn primary">Show results</button>
          <button type="button" className="btn" title="Reset filters" onClick={() => {
            const reset = { search: "", account_type: "", date_from: "", date_to: today(), sort: "account_number", order: "ASC" };
            setForm(reset); setFilters(reset);
          }}><i className="fas fa-rotate-left" /></button>
        </form>

        <div className="tb-period">
          {data && (data.filters.date_from ? `Period: ${shortDate(data.filters.date_from)} – ${shortDate(data.filters.date_to)}` : `All transactions up to ${shortDate(data.filters.date_to) || "today"}`)}
        </div>

        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>#</th>
                <Th col="account_number">Account number</Th>
                <Th col="account_name">Account name</Th>
                <Th col="account_type">Type</Th>
                <Th col="debit_balance" num>Debit balance</Th>
                <Th col="credit_balance" num>Credit balance</Th>
              </tr>
            </thead>
            <tbody>
              {!data && !error && [0, 1, 2, 3].map((r) => <tr key={r}>{[0, 1, 2, 3, 4, 5].map((c) => <td key={c}><div className="skel" /></td>)}</tr>)}
              {data?.accounts.map((a, i) => (
                <tr key={a.account_number}>
                  <td>{i + 1}</td>
                  <td className="mono">{a.account_number}</td>
                  <td style={{ fontWeight: 600, color: "var(--ink)" }}>{a.account_name}</td>
                  <td><span className="pill-tag">{a.account_type}</span></td>
                  <td className="num mono">{a.debit_balance > 0 ? n2(a.debit_balance) : <span style={{ color: "#c7c4bc" }}>–</span>}</td>
                  <td className="num mono">{a.credit_balance > 0 ? n2(a.credit_balance) : <span style={{ color: "#c7c4bc" }}>–</span>}</td>
                </tr>
              ))}
            </tbody>
            {data && data.accounts.length > 0 && (
              <tfoot>
                <tr><td colSpan={4}>Total</td><td className="num mono">TZS {n2(data.total_debit)}</td><td className="num mono">TZS {n2(data.total_credit)}</td></tr>
                {!data.balanced && <tr><td colSpan={4} style={{ color: "var(--danger)" }}>Difference</td><td /><td className="num mono" style={{ color: "var(--danger)" }}>TZS {n2(data.difference)}</td></tr>}
              </tfoot>
            )}
          </table>
          {data && data.accounts.length === 0 && <div className="empty"><i className="fas fa-inbox" />No accounts found. Try adjusting your filters.</div>}
        </div>
      </div>

      {data && data.type_stats.length > 0 && (
        <section className="panel" style={{ padding: "1.25rem" }}>
          <h3 className="tb-h3">Summary by account type</h3>
          <div className="tb-types">
            {data.type_stats.map((s) => (
              <div key={s.type} className="tb-type">
                <strong>{s.type}</strong>
                <span>{s.count} account{s.count === 1 ? "" : "s"}</span>
                <div><small>Debit</small><b className="mono" style={{ color: "var(--up)" }}>{n0(s.debit)}</b></div>
                <div><small>Credit</small><b className="mono" style={{ color: "var(--danger)" }}>{n0(s.credit)}</b></div>
              </div>
            ))}
          </div>
        </section>
      )}
      <p className="tb-generated">Generated on {new Date().toLocaleString("en-GB")}</p>
    </main>
  );
}
